import { GridHookForm } from 'vnetwork-ui'
import useUploadFile from '@/hooks/useUploadFile'
import { STORGE_PREFIXS } from '@/modules/Contract/modules/Process/constants'
import {
    createContractSample,
    getMappingContractSampleForProduct,
    updateContractSample,
} from '@/modules/Contract/modules/Sample/services'
import {
    SampleContract,
    SampleContractCreateParams,
} from '@/modules/Contract/modules/Sample/types'
import { Attachment } from '@/modules/Upload/types'
import { ModalProps } from '@/types/shared'
import { yupResolver } from '@hookform/resolvers/yup'
import { useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Dialog } from 'vnetwork-ui'
import { schemaCreateSampleContractModal } from './schema'
import { formatPGDate, renderFileItem } from '@/utils/function'
import { onDelete, onUpload } from '@/components/Attachment/functions'
import { SAMPLE_CONTRACT_TYPES } from '@/modules/Contract/constants'
import { useMemo } from 'react'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'

export interface CreateSampleContractModalProps extends ModalProps {
    sampleContract?: SampleContract
    mode?: 'create' | 'update'
}

const CreateSampleContractModal = ({
    onClose,
    open,
    sampleContract,
    mode = 'create',
}: CreateSampleContractModalProps) => {
    const { t } = useTranslation()
    const queryClient = useQueryClient()

    const { data: mappingContractSampleForProduct } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.CONTRACTS_SAMPLES_MAPPING],
        queryFn: getMappingContractSampleForProduct,
    })
    const defaultContractType = useMemo(() => {
        if (sampleContract?.sla_enable) {
            return SAMPLE_CONTRACT_TYPES(t)[1].value
        } else if (sampleContract?.nda_enable) {
            return SAMPLE_CONTRACT_TYPES(t)[2].value
        } else {
            return SAMPLE_CONTRACT_TYPES(t)[0].value
        }
    }, [sampleContract?.sla_enable, sampleContract?.nda_enable])

    const formControl = useForm<
        SampleContractCreateParams & {
            attachments: File[] | Attachment[]
            contract_type: string
        }
    >({
        defaultValues: {
            contract_number_id: sampleContract?.contract_number_id || '',
            contract_key: sampleContract?.contract_key || '',
            contract_note: sampleContract?.contract_note || '',
            contract_effective_date: dayjs(
                sampleContract?.contract_effective_date
            ).format('YYYY-MM-DD'),
            contract_type: defaultContractType,
            contract_expiry_date: dayjs(
                sampleContract?.contract_expiry_date
            ).format('YYYY-MM-DD'),
            product_group_name: sampleContract?.product_group_name || '',
            attachments: sampleContract?.attachments || [],
        },
        resolver: yupResolver(schemaCreateSampleContractModal as any),
    })

    const { mutateAsync: create, isPending } = useMutationWithHandler(
        { mutationFn: createContractSample },
        {
            reactQueryKeys: QUERY_KEYS.CONTRACTS,
            successMessage: t('create-sample-contract-success'),
            onSuccess: () => {
                onClose()
                queryClient.refetchQueries({
                    queryKey: [QUERY_KEYS.CONTRACTS_SAMPLES],
                })
            },
        }
    )

    const { mutateAsync: update, isPending: updatePending } =
        useMutationWithHandler(
            { mutationFn: updateContractSample },
            {
                reactQueryKeys: QUERY_KEYS.CONTRACTS,
                successMessage: t('update-sample-contract-success'),
                onSuccess: () => {
                    onClose()
                    queryClient.refetchQueries({
                        queryKey: [QUERY_KEYS.CONTRACTS_SAMPLES],
                    })
                },
            }
        )
    const { uploadAsync } = useUploadFile({
        prefix: STORGE_PREFIXS.PARTNER,
    })

    const onSubmit = async (
        values: SampleContractCreateParams & {
            attachments: File[] | Attachment[]
            contract_type: string
        }
    ) => {
        const { attachments, attachments_to_delete } = await onUpload({
            oldAttachments: sampleContract?.attachments || [],
            attachments: values?.attachments as File[] | Attachment[],
            uploadAsync: uploadAsync as (
                files: File[]
            ) => Promise<{ status: string; value: Attachment }[]>,
        })

        mode === 'update' && sampleContract
            ? await update({
                  data: {
                      ...values,
                      contract_effective_date: formatPGDate(
                          values?.contract_effective_date || '',
                          'YYYY-MM-DD'
                      ),
                      contract_expiry_date: formatPGDate(
                          values?.contract_expiry_date || '',
                          'YYYY-MM-DD'
                      ),
                      sla_enable:
                          values?.contract_type ===
                          SAMPLE_CONTRACT_TYPES(t)[1].value,
                      nda_enable:
                          values?.contract_type ===
                          SAMPLE_CONTRACT_TYPES(t)[2].value,
                  },
                  attachments,
                  attachments_to_delete,
                  contractID: sampleContract?.contract_id,
              })
            : await create({
                  data: {
                      ...values,
                  },
                  attachments,
              })
    }
    return (
        <Dialog
            title={t(`${mode}-sample-contract`)}
            onClose={onClose}
            open={open}
        >
            <GridHookForm
                formControl={formControl}
                actions={[
                    {
                        title: t('cancel'),
                        type: 'button',
                        variant: 'outlined',
                        handler: onClose,
                        disabled: isPending || updatePending,
                    },
                    {
                        title: t(`${mode}-sample-contract`),
                        type: 'submit',
                        variant: 'contained',
                        loading: isPending || updatePending,
                        disabled: isPending || updatePending,
                    },
                ]}
                onSubmit={onSubmit}
                data={[
                    {
                        formItemProps: {
                            name: 'contract_number_id',
                            type: 'textfield',
                            label: t('contract_number_id'),
                            placeholder: t('contract_number_id'),
                        },
                        className: 'col-span-4',
                    },
                    {
                        formItemProps: {
                            name: 'contract_key',
                            type: 'textfield',
                            label: t('contract_key'),
                            placeholder: t('contract_key'),
                        },
                        className: 'col-span-4',
                    },
                    {
                        formItemProps: {
                            name: 'contract_effective_date',
                            type: 'datepicker',
                            label: t('samples.contract_effective_date'),
                            placeholder: t('samples.contract_effective_date'),
                        },
                        className: 'col-span-2',
                    },
                    {
                        formItemProps: {
                            name: 'contract_expiry_date',
                            type: 'datepicker',
                            label: t('samples.contract_expiry_date'),
                            placeholder: t('samples.contract_expiry_date'),
                        },
                        className: 'col-span-2',
                    },
                    // product group
                    {
                        formItemProps: {
                            name: 'product_group_name',
                            type: 'select',
                            label: t('samples.product_group_name'),
                            placeholder: t('samples.product_group_name'),
                            options: mappingContractSampleForProduct,
                        },
                        className: 'col-span-4',
                    },
                    {
                        formItemProps: {
                            name: 'contract_type',
                            type: 'select',
                            label: t('samples.contract_type'),
                            options: SAMPLE_CONTRACT_TYPES(t),
                        },
                        className: 'col-span-4',
                    },
                    {
                        formItemProps: {
                            multiple: true,
                            name: 'attachments',
                            type: 'files',
                            renderFileItem: (file: File | Attachment) => {
                                return renderFileItem(file, (file) => {
                                    const newAttachments = onDelete({
                                        attachments:
                                            formControl.getValues(
                                                'attachments'
                                            ),
                                        file,
                                    })
                                    formControl.setValue(
                                        'attachments',
                                        newAttachments
                                    )
                                })
                            },
                            label: t('attachments'),
                            placeholder: t('attachments'),
                        },
                        className: 'col-span-4',
                    },
                    {
                        formItemProps: {
                            name: 'contract_note',
                            type: 'textarea',
                            label: t('contract_note'),
                            placeholder: t('contract_note'),
                        },
                        className: 'col-span-4',
                    },
                ]}
            />
        </Dialog>
    )
}

export default CreateSampleContractModal
